/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { Button, Card, Col, Form, Radio, Row, Switch } from "antd";
import IconJobCosting from "../../../atoms/CustomIcons/IconJobCosting";
import { InputNumberField } from "../../../atoms/FormFields";
import { useAppDispatch, useAppSelector } from "../../../config/hooks";
import { getCompanyFieldsById } from "../TagsCategories/ducks/actions";
import { SuccessMessage } from "../../../atoms/Toasts/SuccessMessage";
import { ErrorMessage } from "../../../atoms/Toasts/ErrorMessage";
import { updateCompanySettings } from "../Settings/ducks/services";
import FullPageLayout from "../../../templates/FullPageLayout";

export default function JobCosting() {
  const { control, handleSubmit, setValue } = useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);

  const [driveTimeAllocation, setDriveTimeAllocation] =
    useState<boolean>(false);
  // const [overtimeAllocation, setOvertimeAllocation] = useState<boolean>(false);

  const [driveTimeData, setDriveTimeData] = useState<string>(
    "INCLUDE_DRIVE_TIME_TO_DESTINATION"
  );
  const [overtimeData, setOvertimeData] = useState<string>(
    "PROPORTIONALLY_TO_ALL_VISIT_OF_THE_WEEK"
  );

  const dispatch = useAppDispatch();
  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  // const serviceLineId = pathSegments[pathSegments.length - 2];

  const settingsData = useAppSelector((state) => state?.tags?.tagData);

  useEffect(() => {
    if (companyId) {
      const payload = {
        company_id: companyId,
        module: "JOB_COSTING",
      };

      dispatch(getCompanyFieldsById(payload));
    }
  }, [dispatch, companyId]);

  useEffect(() => {
    if (settingsData) {
      const driveTimeData: any = settingsData?.data?.find(
        (item: { name: string; value: string }) =>
          item?.name === "ALLOCATE_DRIVE_TIME_TO_LABOR_COST_ENABLED"
      );
      const overTimeData: any = settingsData?.data?.find(
        (item: { name: string; value: string }) =>
          item?.name === "OVERTIME_ALLOCATION_ENABLED"
      );

      const overTimeRateData: any = settingsData?.data?.find(
        (item: { name: string; value: string }) =>
          item?.name === "OVERTIME_RATE_MULTIPLIER_VALUE"
      );

      if (driveTimeData) {
        setDriveTimeAllocation(driveTimeData?.value);
        setDriveTimeData(
          driveTimeData?.value || "INCLUDE_DRIVE_TIME_TO_DESTINATION"
        );
      }

      if (overTimeData) {
        // setOvertimeAllocation(overTimeData?.value);
        setOvertimeData(
          overTimeData?.value || "PROPORTIONALLY_TO_ALL_VISIT_OF_THE_WEEK"
        );
      }
      if (overTimeRateData) {
        setValue(
          "OVERTIME_RATE_MULTIPLIER_VALUE",
          parseFloat(overTimeRateData?.value)
        );
      }
    }
  }, [settingsData, setValue]);

  const onSubmit = (data: any) => {
    console.log("Form Data:", data);

    setLoading(true);
    const payload = [];

    if (driveTimeAllocation) {
      payload.push({
        company_id: companyId,
        // service_line_id: serviceLineId,
        module: "JOB_COSTING",
        name: "ALLOCATE_DRIVE_TIME_TO_LABOR_COST_ENABLED",
        value: driveTimeData,
      });
    }

    if (overtimeData) {
      payload.push({
        company_id: companyId,
        // service_line_id: serviceLineId,
        module: "JOB_COSTING",
        name: "OVERTIME_ALLOCATION_ENABLED",
        value: overtimeData,
      });
    }

    if (data?.OVERTIME_RATE_MULTIPLIER_VALUE) {
      payload.push({
        company_id: companyId,
        // service_line_id: serviceLineId,
        module: "JOB_COSTING",
        name: "OVERTIME_RATE_MULTIPLIER_VALUE",
        value: data?.OVERTIME_RATE_MULTIPLIER_VALUE.toString(),
      });
    }

    updateCompanySettings(payload)
      .then(() => {
        setLoading(false);
        SuccessMessage("Job costing updated");
        navigate(`/company-settings/${companyId}`);
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  return (
    <FullPageLayout loading={loading}>
      <div
        className="service_line_settings"
        style={{ width: "800px", margin: "0 auto", paddingBottom: 90 }}
      >
        <div>
          <h2 className="tabs_content_heading">
            <IconJobCosting /> Job costing
          </h2>
          <p className="tabs_content_subheading">
            Configure how job costs are calculated to align reports with your
            company’s operations.
          </p>
        </div>

        <Form
          layout="vertical"
          onFinish={handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Enter") e.preventDefault();
          }}
        >
          <Card className="cs_management_card">
            <div className="cs_title_bar">
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div className="icon_avatar">
                  <img
                    src="/images/settings/cs_truck.svg"
                    alt="customer management"
                  />
                </div>
                <div>
                  <h3> Drive time allocation </h3>
                  <p>Allocate drive time to direct labor cost.</p>
                </div>
              </div>
              <div>
                <Switch
                  checked={driveTimeAllocation}
                  onChange={setDriveTimeAllocation}
                  disabled={true}
                />
              </div>
            </div>

            <Radio.Group
              className="tracking_option_radio"
              value={driveTimeData}
              onChange={(e) => setDriveTimeData(e.target.value)}
            >
              <Row>
                <Col span={24}>
                  <Radio
                    value="INCLUDE_DRIVE_TIME_TO_DESTINATION"
                    style={{ width: "100%" }}
                  >
                    <div className="bordered_card">
                      <h4 style={{ marginBottom: "6px" }}>
                        Include drive time in direct costs of destination
                        property
                      </h4>
                      <p style={{ color: "#6C7478", marginBottom: "0px" }}>
                        If selected, crew drive time to the next property will
                        be allocated to that property. The drive time back to
                        the crew yard is counted as overhead (indirect cost).
                      </p>
                    </div>
                  </Radio>
                </Col>
              </Row>
              {/* <Row style={{ marginBottom: "12px" }}>
                <Col span={24}>
                  <Radio
                    value="SPLIT_BETWEEN_ORIGIN_AND_DESTINATION"
                    style={{ width: "100%" }}
                  >
                    <div className="bordered_card">
                      <h4 style={{ marginBottom: "6px" }}>
                        Split drive time between origin and destination
                        properties
                      </h4>
                      <p style={{ color: "#6C7478", marginBottom: "0px" }}>
                        If enabled, drive time between two properties is split
                        evenly. Half of the time from and back to the crew yard
                        is counted as overhead (indirect cost).
                      </p>
                    </div>
                  </Radio>
                </Col>
              </Row>
              <Row style={{ marginBottom: "0px" }}>
                <Col span={24}>
                  <Radio
                    value="DISTRIBUTE_PROPORTIONALLY_AMONG_PROPERTIES"
                    style={{ width: "100%" }}
                  >
                    <div className="bordered_card">
                      <h4 style={{ marginBottom: "6px" }}>
                        Distribute drive time costs proportionally
                      </h4>
                      <p style={{ color: "#6C7478", marginBottom: "0px" }}>
                        If selected, crew drive time will be distributed
                        proportionally among the properties on the route.
                      </p>
                    </div>
                  </Radio>
                </Col>
              </Row> */}
            </Radio.Group>
          </Card>

          <Card className="cs_management_card" style={{ position: "relative" }}>
            <div
              className="overlay"
              style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0,
                borderRadius: "20px",
                backgroundColor: "#fff6f6",
                zIndex: 99,
                opacity: 0.4,
              }}
            ></div>
            <div className="cs_title_bar">
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div className="icon_avatar">
                  <img
                    src="/images/settings/icon_overtime_allocation.svg"
                    alt="customer management"
                  />
                </div>
                <div>
                  <h3> Overtime allocation </h3>
                  <p>
                    Allocate overtime to jobs to clearly see where your losses
                    are.
                  </p>
                </div>
              </div>
              <div>
                <Switch
                  // checked={overtimeAllocation}
                  checked={false}
                  // onChange={setOvertimeAllocation}
                />
              </div>
            </div>

            <Radio.Group
              className="tracking_option_radio"
              value={overtimeData}
              onChange={(e) => setOvertimeData(e.target.value)}
            >
              <Row style={{ marginBottom: "12px" }}>
                <Col span={24}>
                  <Radio
                    value="PROPORTIONALLY_TO_ALL_VISIT_OF_THE_WEEK"
                    style={{ width: "100%" }}
                  >
                    <div className="bordered_card">
                      <h4 style={{ marginBottom: "6px" }}>
                        Proportionally to all visits of the week
                      </h4>
                      <p style={{ color: "#6C7478", marginBottom: "0px" }}>
                        Description
                      </p>
                    </div>
                  </Radio>
                </Col>
              </Row>
              <Row>
                <Col span={24}>
                  <Radio
                    value="VISIT_CAUSED_OVERTIME_GET_MORE_OVERTIME"
                    style={{ width: "100%" }}
                  >
                    <div className="bordered_card">
                      <h4 style={{ marginBottom: "6px" }}>
                        Visits caused overtime get more overtime
                      </h4>
                      <p style={{ color: "#6C7478", marginBottom: "0px" }}>
                        Description
                      </p>
                    </div>
                  </Radio>
                </Col>
              </Row>
            </Radio.Group>
          </Card>

          <Card className="cs_management_card" style={{ position: "relative" }}>
            <div
              className="overlay"
              style={{
                position: "absolute",
                height: "100%",
                width: "100%",
                top: 0,
                left: 0,
                borderRadius: "20px",
                backgroundColor: "#fff6f6",
                zIndex: 99,
                opacity: 0.4,
              }}
            ></div>
            <div className="cs_title_bar">
              <div
                style={{ display: "flex", alignItems: "center", gap: "16px" }}
              >
                <div className="icon_avatar">
                  <img
                    src="/images/settings/icon_rate.svg"
                    alt="customer management"
                  />
                </div>
                <div>
                  <h3> Overtime rate multiplier </h3>
                  <p>
                    Set the multiplier applied to the standard labor rate when
                    hours are billed as overtime.
                  </p>
                </div>
              </div>
            </div>
            <div style={{ maxWidth: "317px" }}>
              <InputNumberField
                fieldName="OVERTIME_RATE_MULTIPLIER_VALUE"
                label="Overtime rate multiplier"
                control={control}
                defaultValue={1.5}
                iProps={{
                  placeholder: "Overtime rate multiplier",
                  min: 1,
                }}
                isRequired
                rules={{
                  required: "This field is required.",
                }}
                className="mb_0"
              />
            </div>
          </Card>

          <div className="fixed_btm_card">
            <Card>
              <div className="centered">
                <Button
                  htmlType="button"
                  className="outlined-btn"
                  onClick={() => navigate(`/company-settings/${companyId}`)}
                >
                  Cancel
                </Button>
                <Button htmlType="submit" type="primary" className="mint-btn">
                  Save changes
                </Button>
              </div>
            </Card>
          </div>
        </Form>
      </div>
    </FullPageLayout>
  );
}
